import { Box, Stack } from '@mui/material'
import React, { useCallback, useEffect } from 'react'
import { SectionBox } from '../../../../../HelperFunctions/HelperFunctions'
import { useFormikContext } from 'formik'
import PayrollsPopup from '../../../../../Components/PayrollPopup';
import { useAlerts } from '../../../../../Hooks';
import { FieldRow, CustomDatePicker, CustomSelect, CustomCheckboxGroup } from '../../../../../HelperFunctions/EmployeeFormHelpers';
import { useSnackbar } from 'notistack';
import dayjs from 'dayjs';
import { MARITAL_STATUS_MAP } from '../../../../../Enums/Enum';

// --- Constants ---

const ADDITIONAL_INFO_FIELDS = [
    { name: "homeNum", heading: "Home Number" },
    { name: "alternativeNum", heading: "Alternate Number" },
    { name: "workNum", heading: "Work Number" },
    { name: "faxNum", heading: "Fax Number" },
];

// --- Validation Helpers ---
const validatePhoneNumber = (value) => {
    const val = value.replace(/\D/g, "");
    return (val.length > 0 && val.length < 10) ? "must be exactly 10 digits" : null;
};

const validateAccountNumber = (value) => {
    const val = value.replace(/\D/g, "");
    return (val.length > 0 && (val.length < 12 || val.length > 15)) ? "must be between 12 and 15 digits" : null;
};

const validateDateSequence = (startDate, endDate, startLabel, endLabel) => {
    if (startDate && endDate && dayjs(startDate).isAfter(dayjs(endDate), 'day')) {
        return `${startLabel} should not be greater than the ${endLabel}.`;
    }
    return null;
};

const EmploymentInfo = ({ token }) => {
    const { values, handleChange, setFieldValue, handleBlur, setFieldTouched, setFieldError } = useFormikContext();
    const { popup: alertPopup, hidePopup } = useAlerts();
    const { enqueueSnackbar } = useSnackbar();

    const onChange = useCallback(handleChange, [handleChange]);

    // --- Handlers: Phone Validation ---
    const handlePhoneChange = (e, label) => {
        if (/^[0-9]*$/.test(e.target.value)) {
            handleChange(e);
        } else {
            enqueueSnackbar(`${label} must be numeric.`, { variant: "error" });
        }
    };
    //         newlyAdded = updatedBanks.find((b) => b.label?.toLowerCase() === resData.Name.toLowerCase());
    //     }
    
    const handlePhoneBlur = useCallback((e, fieldName, label) => {
        handleBlur(e);
        const error = validatePhoneNumber(e.target.value);
        if (error) {
            setFieldValue(fieldName, "");
            setFieldTouched(fieldName, true, false);
            enqueueSnackbar(`${label} ${error}`, { variant: "error" });
        } else {
            setFieldError(fieldName, undefined);
        }
    }, [handleBlur, setFieldValue, setFieldTouched, setFieldError, enqueueSnackbar]);

    // --- Handlers: Account Number Validation ---
    const handleAccountNumberBlur = useCallback((e, fieldName, label) => {
        handleBlur(e);
        const error = validateAccountNumber(e.target.value);
        if (error) {
            setFieldValue(fieldName, "");
            setFieldTouched(fieldName, true, false);
            enqueueSnackbar(`${label} ${error}`, { variant: "error" });
        } else {
            setFieldError(fieldName, undefined);
        }
    }, [handleBlur, setFieldValue, setFieldTouched, setFieldError, enqueueSnackbar]);

    // --- Handlers: Date Sequences ---
    const handleHireDateChange = useCallback((name, val) => {
        const startDate = values.employmentInfo.employeeDetails.startDate;
        const error = validateDateSequence(val, startDate, "Hire Date", "Start Date");
        if (error) {
            enqueueSnackbar(error, { variant: "error" });
            setFieldValue(name, "");
            setFieldTouched(name, true, false);
        } else {
            setFieldValue(name, val);
        }
    }, [values.employmentInfo.employeeDetails.startDate, setFieldValue, setFieldTouched, enqueueSnackbar]);

    const handleStartDateChange = useCallback((name, val) => {
        const hireDate = values.employmentInfo.employeeDetails.hireDate;
        const error = validateDateSequence(hireDate, val, "Hire Date", "Start Date");
        if (error) {
            enqueueSnackbar(error, { variant: "error" });
            setFieldValue(name, "");
            setFieldTouched(name, true, false);
        } else {
            setFieldValue(name, val);
        }
    }, [values.employmentInfo.employeeDetails.hireDate, setFieldValue, setFieldTouched, enqueueSnackbar]);

    // --- Handlers: Decimal / Currency Inputs ---
    const handleDecimalChange = useCallback((e, fieldPath, label) => {
        const val = e.target.value;
        if (/[^0-9.]/.test(val) || (val.match(/\./g) || []).length > 1) {
            enqueueSnackbar(`${label} must be numeric.`, { variant: "error" });
        } else {
            setFieldValue(fieldPath, val);
        }
    }, [setFieldValue, enqueueSnackbar]);

    const handleDecimalFocus = useCallback((fieldPath, currentValue) => {
        if (currentValue === "0.00") {
            setFieldValue(fieldPath, "");
        }
    }, [setFieldValue]);

    const handleDecimalBlur = useCallback((e, fieldPath) => {
        handleBlur(e);
        let num = parseFloat(e.target.value);
        if (isNaN(num)) num = 0;
        const roundedVal = (Math.round((num + Number.EPSILON) * 100) / 100).toFixed(2);
        setFieldValue(fieldPath, roundedVal);
    }, [handleBlur, setFieldValue]);

    // --- Effects ---
    useEffect(() => {
        if (values.employmentInfo?.employeeDetails?.usCitizen && values.employmentInfo?.employeeDetails?.i9ExpirationDate) {
            setFieldValue("employmentInfo.employeeDetails.i9ExpirationDate", "");
        }
    }, [values.employmentInfo?.employeeDetails?.usCitizen, values.employmentInfo?.employeeDetails?.i9ExpirationDate, setFieldValue]);

    return (
        <>
        
            <Stack direction="row">

                <Stack height={"100%"} width={"auto"} gap={1} pr={2}>

                    <SectionBox height={"auto"} title="Additional Info">

                        <Box display="flex" flexDirection="column" gap={1}>
                            <CustomDatePicker
                                heading="DOB"
                                name="employmentInfo.employeeDetails.dob"
                                value={values.employmentInfo.employeeDetails.dob}
                                setFieldValue={setFieldValue}
                                disableFuture={true}
                            />
                            <CustomSelect
                                heading="Marital Status"
                                name="maritalStatus"
                                value={values.maritalStatus}
                                onChange={onChange}
                                options={[
                                    { value: 0, label: "--Select--" },
                                    ...Object.entries(MARITAL_STATUS_MAP).map(
                                        ([label, value]) => ({ label, value })
                                    ),
                                ]}
                            />
                            {ADDITIONAL_INFO_FIELDS.map(({ name: fieldName, heading }) => (
                                <FieldRow
                                    key={fieldName}
                                    heading={heading}
                                    name={`employmentInfo.additionalInfo.${fieldName}`}
                                    value={values.employmentInfo.additionalInfo[fieldName]}
                                    onChange={(e) => handlePhoneChange(e, heading)}
                                    onBlur={(e) => handlePhoneBlur(e, `employmentInfo.additionalInfo.${fieldName}`, heading)}
                                    inputProps={{ maxLength: 10 }}
                                />
                            ))}
                            <FieldRow
                                heading="Bank Name"
                                name="employmentInfo.additionalInfo.bankName"
                                value={values.employmentInfo.additionalInfo.bankName}
                                onChange={onChange}
                            />

                            <FieldRow
                                heading="Bank Account Number"
                                name="employmentInfo.additionalInfo.bankACNum"
                                value={values.employmentInfo.additionalInfo.bankACNum}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    if (/^\d*$/.test(val)) {
                                        setFieldValue("employmentInfo.additionalInfo.bankACNum", val);
                                    }
                                }}
                                inputProps={{ maxLength: 15 }}
                                onBlur={(e) => handleAccountNumberBlur(e, "employmentInfo.additionalInfo.bankACNum", "Bank Account Number")}
                            />
                        </Box>

                    </SectionBox>

                    <SectionBox height={"auto"} title="Employee Details">
                        <Stack gap={1}>
                            <CustomDatePicker
                                heading="Hire Date"
                                name="employmentInfo.employeeDetails.hireDate"
                                value={values.employmentInfo.employeeDetails.hireDate}
                                setFieldValue={handleHireDateChange}
                            />
                            <CustomDatePicker
                                heading="Start Date"
                                name="employmentInfo.employeeDetails.startDate"
                                value={values.employmentInfo.employeeDetails.startDate}
                                setFieldValue={handleStartDateChange}
                            />
                            <CustomCheckboxGroup
                                heading="Employee Status"
                                fields={[
                                    {
                                        name: "employmentInfo.employeeDetails.usCitizen",
                                        label: "US Citizen",
                                        value: values.employmentInfo.employeeDetails.usCitizen,
                                        onChange: (checked) => {
                                            setFieldValue("employmentInfo.employeeDetails.usCitizen", checked);
                                            if (checked) {
                                                setFieldValue("employmentInfo.employeeDetails.i9OnFile", false);
                                            }
                                        }
                                    },
                                    {
                                        name: "employmentInfo.employeeDetails.i9OnFile",
                                        label: "I-9 On File",
                                        value: values.employmentInfo.employeeDetails.i9OnFile,
                                        onChange: (checked) => {
                                            setFieldValue("employmentInfo.employeeDetails.i9OnFile", checked);
                                            if (checked) {
                                                setFieldValue("employmentInfo.employeeDetails.usCitizen", false);
                                            }
                                        }
                                    },
                                ]}
                                setFieldValue={setFieldValue}
                            />
                            <CustomDatePicker
                                heading="I9 Expiration Date"
                                name="employmentInfo.employeeDetails.i9ExpirationDate"
                                value={values.employmentInfo.employeeDetails.i9ExpirationDate}
                                setFieldValue={setFieldValue}
                                disabled={values.employmentInfo.employeeDetails.usCitizen}
                            />

                        </Stack>
                    </SectionBox>
                </Stack>

                
                <Stack height={"100%"} width={"auto"} gap={1} pr={2}>
                    <SectionBox height={"auto"} title="Job Details">
                        <Stack gap={1}>

                            <FieldRow
                                heading="Current LCA (State)"
                                name="employmentInfo.jobDetails.lcaState"
                                value={values.employmentInfo.jobDetails.lcaState}
                                onChange={(e) => handleDecimalChange(e, "employmentInfo.jobDetails.lcaState", "Current LCA (State)")}
                                onFocus={() => handleDecimalFocus("employmentInfo.jobDetails.lcaState", values.employmentInfo.jobDetails.lcaState)}
                                onBlur={(e) => handleDecimalBlur(e, "employmentInfo.jobDetails.lcaState")}
                                inputProps={{ style: { padding: "0px 10px", textAlign: "right", fontSize: "12px" } }}
                            />

                            <FieldRow
                                heading="Current LCA (GC)"
                                name="employmentInfo.jobDetails.lcaGC"
                                value={values.employmentInfo.jobDetails.lcaGC}
                                onChange={(e) => handleDecimalChange(e, "employmentInfo.jobDetails.lcaGC", "Current LCA (GC)")}
                                onFocus={() => handleDecimalFocus("employmentInfo.jobDetails.lcaGC", values.employmentInfo.jobDetails.lcaGC)}
                                onBlur={(e) => handleDecimalBlur(e, "employmentInfo.jobDetails.lcaGC")}
                                inputProps={{ style: { padding: "0px 10px", textAlign: "right", fontSize: "12px" } }}
                            />

                            {/* Job Type */}
                            <CustomSelect
                                heading="Job Type"
                                name="employmentInfo.jobDetails.jobType"
                                value={values.employmentInfo.jobDetails.jobType}
                                onChange={onChange}
                                options={[
                                    { value: 0, label: "--Select--" },
                                    { value: 1, label: "Fulltime" },
                                    { value: 2, label: "Part-time" },
                                    { value: 3, label: "Contract" },
                                ]}
                            />
                        </Stack>
                    </SectionBox>

                    <SectionBox height={"auto"} title="Termination Details">
                        <Stack gap={1}>

                            <CustomSelect
                                heading="Termination Type"
                                name="employmentInfo.terminationDetails.terminationType"
                                value={values.employmentInfo.terminationDetails.terminationType}
                                onChange={onChange}
                                options={[
                                    { value: 0, label: "--Select--" },
                                    { value: 1, label: "Voluntary" },
                                    { value: 2, label: "Involuntary" }
                                ]}
                            />
                            <FieldRow
                                heading="Termination Reason"
                                name="employmentInfo.terminationDetails.terminationReason"
                                value={values.employmentInfo.terminationDetails.terminationReason}
                                onChange={onChange}
                            />

                            <CustomDatePicker
                                heading="Last Day Worked"
                                name="employmentInfo.terminationDetails.lastDayWorked"
                                value={values.employmentInfo.terminationDetails.lastDayWorked}
                                setFieldValue={setFieldValue}
                            />
                            <CustomDatePicker
                                heading="Last Day on Benefits"
                                name="employmentInfo.terminationDetails.lastDayOnBenefits"
                                value={values.employmentInfo.terminationDetails.lastDayOnBenefits}
                                setFieldValue={setFieldValue}
                            />
                            <CustomDatePicker
                                heading="Release Date"
                                name="employmentInfo.terminationDetails.releaseDate"
                                value={values.employmentInfo.terminationDetails.releaseDate}
                                setFieldValue={setFieldValue}
                            />
                        </Stack>

                    </SectionBox>

                </Stack>



            </Stack>
            <PayrollsPopup
                open={alertPopup.open}
                popupType={alertPopup.type}
                message={alertPopup.message}
                onConfirm={alertPopup.onConfirm}
                onCancel={hidePopup}
            />

        </>
    )
}

export default EmploymentInfo;
